import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AlphaCoreLogo from "@/components/AlphaCoreLogo";
import { FALLBACK_MODULES, LAUNCH_MODULE_META, MODULE_COLOR_RGB } from "@/lib/modules";
import {
  ArrowRight, Play, Check, Menu, X, Layers, Bot, Users, ShieldCheck, Zap, TrendingUp,
  MessageCircle, Image as ImageIcon, Globe, Search, Workflow, Code2, Brain,
} from "lucide-react";

const GRADIENT = "linear-gradient(90deg,#FF3AA8,#8A52FF)";
const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "Solutions", href: "#modules" },
  { label: "Resources", href: "#why" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
];

const FEATURES = [
  { icon: Layers, rgb: MODULE_COLOR_RGB.blue, title: "One Workspace", desc: "Chat, images, websites, research, swarms and code — one login, one credit balance, zero tool-switching." },
  { icon: Bot, rgb: MODULE_COLOR_RGB.purple, title: "AI Agents", desc: "Launch multi-agent swarms that research, plan, create and review work autonomously." },
  { icon: Users, rgb: MODULE_COLOR_RGB.green, title: "Built for Creators & Teams", desc: "Projects keep every chat, asset and generation organized and shareable." },
  { icon: ShieldCheck, rgb: MODULE_COLOR_RGB.pink, title: "Security First", desc: "Strict ownership on every file and project, role-based access and audited admin actions." },
];

const WHY = [
  { icon: Zap, rgb: MODULE_COLOR_RGB.amber, title: "All-in-one platform", desc: "Stop paying for six subscriptions. AlphaCore replaces your chat AI, image tool, site builder, research assistant and code copilot." },
  { icon: TrendingUp, rgb: MODULE_COLOR_RGB.green, title: "Save time & scale", desc: "Memory-aware AI that learns your brand and preferences, so every output starts 80% done." },
];

const PLANS = [
  { name: "Standard", price: "$10.99", per: "/month", credits: "500 credits", features: ["All six modules", "500 monthly credits", "Project workspaces", "Email support"] },
  { name: "Premium", price: "$16.99", per: "/month", credits: "750 credits", features: ["Everything in Standard", "750 monthly credits", "Priority generation", "Priority support"], popular: true },
  { name: "Ultimate API", price: "Contact Us", per: "", credits: "Custom volume", features: ["API access", "Custom credit volume", "Dedicated support", "Custom integrations"], contact: true },
];

const MINI_STATS = [
  { v: "12,450", l: "Total Credits" },
  { v: "24", l: "Projects" },
  { v: "128", l: "AI Generations" },
  { v: "98%", l: "Success Rate" },
];
const MINI_MODULES = [
  { icon: MessageCircle, rgb: MODULE_COLOR_RGB.pink, n: "AlphaCore AI" },
  { icon: ImageIcon, rgb: MODULE_COLOR_RGB.green, n: "Images" },
  { icon: Globe, rgb: MODULE_COLOR_RGB.blue, n: "Websites" },
  { icon: Search, rgb: MODULE_COLOR_RGB.amber, n: "Research" },
  { icon: Workflow, rgb: MODULE_COLOR_RGB.purple, n: "Swarm" },
  { icon: Code2, rgb: MODULE_COLOR_RGB.cyan, n: "Code" },
];

function rgba(rgb, a) { return `rgba(${rgb.split(" ").join(",")},${a})`; }

export default function Landing() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const appTo = user ? "/dashboard" : "/signup";

  return (
    <div className="min-h-screen bg-[#07080B] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="sticky top-0 z-50 kr-glass border-b border-white/[0.06]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-6">
          <Link to="/" className="shrink-0"><AlphaCoreLogo size={40} /></Link>
          <div className="hidden lg:flex items-center gap-7 ml-6">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="text-sm text-white/60 hover:text-white transition-colors">{l.label}</a>
            ))}
          </div>
          <div className="flex-1" />
          <Link to="/login" className="hidden sm:block text-sm text-white/70 hover:text-white" data-testid="nav-signin">Sign in</Link>
          <Link to={appTo} data-testid="nav-launch-btn">
            <button className="h-10 px-5 rounded-full text-sm font-semibold text-white flex items-center gap-1.5" style={{ background: GRADIENT }}>
              Launch AlphaCore <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
          <button onClick={() => setMenuOpen((o) => !o)} className="lg:hidden p-2 -mr-1 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Menu" data-testid="landing-hamburger">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-white/[0.06] bg-[#0B0D12] px-4 py-3 space-y-1" data-testid="landing-mobile-menu">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)} className="block py-3 px-2 text-sm text-white/70 hover:text-white min-h-[44px]">{l.label}</a>
            ))}
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-3 px-2 text-sm text-white/70 hover:text-white min-h-[44px]">Sign in</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative max-w-[1280px] mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(138,82,255,0.14), transparent 65%)" }} />
        <div className="relative">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[11px] font-mono tracking-[0.15em]" style={{ borderColor: "rgba(0,229,255,0.4)", color: "#00E5FF" }} data-testid="hero-badge">
            <Brain className="h-3.5 w-3.5" /> THE CORE OF INTELLIGENCE
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6" data-testid="hero-title">
            All the power of AI.
            <br />
            <span style={{ background: "linear-gradient(90deg,#8A52FF,#FF3AA8,#FF8A22)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              One intelligent workspace.
            </span>
          </h1>
          <p className="text-[#9DA3B4] text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
            AlphaCore brings together AI agents, creative tools and automation so you can build, research, create and launch — faster than ever.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={appTo} data-testid="hero-start-free">
              <button className="h-12 px-7 rounded-full font-semibold text-white flex items-center gap-2 shadow-[0_0_30px_rgba(255,58,168,0.3)]" style={{ background: GRADIENT }}>
                Start free <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="#modules" data-testid="hero-watch-demo">
              <button className="h-12 px-7 rounded-full font-semibold border border-white/15 text-white/85 hover:bg-white/5 flex items-center gap-2">
                <Play className="h-4 w-4" /> Watch demo
              </button>
            </a>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/45">
            {["No credit card", "Free 7-day trial", "Cancel anytime"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" style={{ color: "#00DC82" }} /> {t}</span>
            ))}
          </div>
        </div>

        {/* Dashboard preview mock */}
        <div className="relative hidden md:block" data-testid="hero-preview">
          <div className="absolute -top-6 -right-4 w-12 h-12 rounded-2xl flex items-center justify-center border border-white/10 bg-[#101219] shadow-[0_0_24px_rgba(138,82,255,0.35)]"><Brain className="h-5 w-5" style={{ color: "#8A52FF" }} /></div>
          <div className="absolute -bottom-5 -left-5 w-12 h-12 rounded-2xl flex items-center justify-center border border-white/10 bg-[#101219] shadow-[0_0_24px_rgba(0,229,255,0.3)]"><Code2 className="h-5 w-5" style={{ color: "#00E5FF" }} /></div>
          <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <div className="flex items-center gap-2 mb-4">
              <AlphaCoreLogo size={26} variant="mark" />
              <div className="text-sm font-display">Welcome back 👋</div>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {MINI_MODULES.map((m) => (
                <div key={m.n} className="rounded-xl p-3 border" style={{ borderColor: rgba(m.rgb, 0.3), background: `linear-gradient(150deg, ${rgba(m.rgb, 0.1)}, rgba(16,18,25,0.6))` }}>
                  <m.icon className="h-4 w-4" style={{ color: `rgb(${m.rgb})` }} />
                  <div className="text-[11px] mt-2 text-white/85 truncate">{m.n}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2.5 mt-3">
              {MINI_STATS.map((s) => (
                <div key={s.l} className="rounded-xl p-2.5 border border-white/[0.07] bg-white/[0.02] text-center">
                  <div className="font-display text-sm">{s.v}</div>
                  <div className="text-[9px] text-white/40 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 pb-14">
        <div className="kr-overline text-center text-white/35">TRUSTED BY CREATORS, TEAMS & COMPANIES</div>
        <div className="mt-5 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 text-white/30 font-display text-lg">
          {["Google", "Microsoft", "Notion", "Discord", "Figma"].map((b) => <span key={b}>{b}</span>)}
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="max-w-[1280px] mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <h2 className="font-display text-base md:text-lg text-center text-white/60">OUR CORE MODULES</h2>
        <p className="font-display text-3xl sm:text-4xl text-center mt-2 tracking-tight">Six tools. One core.</p>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-modules">
          {FALLBACK_MODULES.map((m) => {
            const meta = LAUNCH_MODULE_META[m.key];
            const rgb = MODULE_COLOR_RGB[meta.color];
            const Icon = meta.icon;
            return (
              <Link key={m.key} to={appTo} className="group rounded-2xl p-5 border transition-all hover:-translate-y-0.5" style={{ borderColor: rgba(rgb, 0.3), background: `linear-gradient(150deg, ${rgba(rgb, 0.08)}, rgba(16,18,25,0.6) 60%)` }} data-testid={`landing-module-${m.key}`}>
                <Icon className="h-7 w-7" style={{ color: `rgb(${rgb})` }} />
                <div className="font-display text-sm sm:text-base mt-3">{m.name}</div>
                <p className="text-xs sm:text-[13px] text-[#9DA3B4] mt-1.5 leading-relaxed">{m.description}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-[1280px] mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <h2 className="font-display text-base md:text-lg text-center text-white/60">EVERYTHING YOU NEED</h2>
        <p className="font-display text-3xl sm:text-4xl text-center mt-2 tracking-tight">Powered by intelligence.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="kr-card p-6">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: rgba(f.rgb, 0.12) }}>
                <f.icon className="h-5 w-5" style={{ color: `rgb(${f.rgb})` }} />
              </span>
              <div className="font-display text-base mt-4">{f.title}</div>
              <p className="text-[13px] text-[#9DA3B4] mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why */}
      <section id="why" className="max-w-[1280px] mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <h2 className="font-display text-base md:text-lg text-center text-white/60">WHY ALPHACORE?</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {WHY.map((w) => (
            <div key={w.title} className="kr-card p-7 flex gap-5">
              <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: rgba(w.rgb, 0.12) }}>
                <w.icon className="h-5 w-5" style={{ color: `rgb(${w.rgb})` }} />
              </span>
              <div>
                <div className="font-display text-lg">{w.title}</div>
                <p className="text-sm text-[#9DA3B4] mt-2 leading-relaxed">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-[1280px] mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <h2 className="font-display text-base md:text-lg text-center text-white/60">PRICING</h2>
        <p className="font-display text-3xl sm:text-4xl text-center mt-2 tracking-tight">Simple plans. Serious power.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.name} className="kr-card p-6 relative" style={p.popular ? { borderColor: "rgba(255,58,168,0.4)" } : {}}>
              {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-mono tracking-wider px-3 py-0.5 rounded-full text-white" style={{ background: GRADIENT }}>MOST POPULAR</span>}
              <div className="font-display text-lg">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-3xl">{p.price}</span>
                <span className="text-xs text-white/40">{p.per}</span>
              </div>
              <div className="text-xs mt-1" style={{ color: "#FFD23F" }}>{p.credits}</div>
              <ul className="mt-4 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-[13px] text-white/70"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: "#00DC82" }} /> {f}</li>
                ))}
              </ul>
              <Link to={p.contact ? "/support" : appTo}>
                <button className={`mt-5 w-full h-11 rounded-xl text-sm font-semibold ${p.popular ? "text-white" : "border border-white/15 text-white/85 hover:bg-white/5"}`} style={p.popular ? { background: GRADIENT } : {}} data-testid={`plan-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {p.contact ? "Contact Sales" : "Request Trial"}
                </button>
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-white/35 mt-5">AlphaCore is currently in trial mode — request access and we'll onboard you.</p>
      </section>

      {/* CTA + Footer */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Start creating with AlphaCore in 30 seconds.</h2>
        <Link to={appTo}>
          <button className="mt-7 h-12 px-8 rounded-full font-semibold text-white inline-flex items-center gap-2 shadow-[0_0_30px_rgba(255,58,168,0.3)]" style={{ background: GRADIENT }} data-testid="footer-cta">
            Launch AlphaCore <ArrowRight className="h-4 w-4" />
          </button>
        </Link>
      </section>

      <footer id="about" className="border-t border-white/[0.06]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <AlphaCoreLogo size={34} />
          <p className="text-xs text-white/40 max-w-sm text-center sm:text-right">
            AlphaCore — The Core of Intelligence. One intelligent workspace for AI chat, images, websites, research, swarms and code.
          </p>
        </div>
        <div className="text-center text-xs text-white/25 pb-8">© 2026 AlphaCore. All rights reserved.</div>
      </footer>
    </div>
  );
}
